import Modal from 'bootstrap/js/src/modal';

const STATUS_PRELOADING = 'status-preloading';

/**
 * @typedef {Object} OriginalPageData
 * @property {string} href
 * @property {string} title
 */

/**
 * @typedef {Object} InspirationUrlData
 * @property {string} api
 * @property {string} url
 */

/**
 * @typedef {Object} PreviousAndNextInspirationUrlData
 * @property {InspirationUrlData} previous
 * @property {InspirationUrlData} next
 */

/**
 * @callback getPreviousAndNextInspirationDataCallback
 * @param {int} shift
 * @returns {PreviousAndNextInspirationUrlData}
 */

class InspirationModal {
    /**
     * @param {HTMLElement} element
     * @param {OriginalPageData} original_page_data
     */
    constructor(element, original_page_data = {}) {
        this.element = element;
        this.modal = new Modal(element, {});
        this.card = element.querySelector('.modal-content .modal-body');
        this.header = element.querySelector('.modal-content .modal-header');

        /**
         * @type {function}
         */
        this.onSwipePrev = null;
        /**
         * @type {function}
         */
        this.onSwipeNext = null;
        /**
         * @type {function}
         */
        this.onClickPrev = null;
        /**
         * @type {function}
         */
        this.onClickNext = null;

        this.preloaded_inspiration_data = {};

        this.element.addEventListener('hidden.bs.modal', () => {
            if (location.href !== original_page_data.href) {
                this.updateUrl(original_page_data.href, original_page_data.title, {
                    type: 'modal_hide',
                });
            }
        });

        // noinspection JSUnresolvedVariable
        this.is_touch = (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0));
        if (this.is_touch) {
            this.swipe_vertical_range = 50;
            this.swipe_start_horizontal_length = 0;
            this.action_ratio_threshold = 0.4;

            this.start_x = 0;
            this.start_y = 0;
            this.swipe_blocked = false;  // is horizontal movement?

            // We're saving event handlers in case we need to remove them later. I will thank myself for that in the future
            this.onTouchStartHandler = (e) => this.onTouchStart(e);
            this.onTouchMoveHandler = (e) => this.onTouchMove(e);
            this.onTouchEndHandler = (e) => this.onTouchEnd(e);
            this.onTouchCancelHandler = (e) => this.onTouchCancel(e);

            this.card.addEventListener('touchstart', this.onTouchStartHandler, {passive: true});
            this.card.addEventListener('touchmove', this.onTouchMoveHandler, {passive: true});
            this.card.addEventListener('touchend', this.onTouchEndHandler, {passive: true});
            this.card.addEventListener('touchcancel', this.onTouchCancelHandler, {passive: true});
        }

        /**
         * @type {getPreviousAndNextInspirationDataCallback}
         */
        this.get_previous_and_next_inspiration_url_data = null;

        this.element.addEventListener('shown.bs.modal', (e) => {
            const first_interactive_el = this.element.querySelector('a[href], button, [tabindex="0"]');
            if (first_interactive_el) {
                first_interactive_el.focus();
            }
        });
    }

    /**
     * @param {string} href
     * @param {string} name
     * @param {Object} data
     */
    updateUrl(href, name, data = {}) {
        window.history.pushState(data, name, href);
        document.title = name;
    };

    /**
     * @param {string} url
     */
    preloadData(url) {
        if (!(url in this.preloaded_inspiration_data)) {
            this.preloaded_inspiration_data[url] = STATUS_PRELOADING;

            fetch(url)
                .then(response => response.json())
                .then(data => {
                    this.preloaded_inspiration_data[url] = data;
                })
                .catch((error) => {
                    console.error('Error while fetching data:', error);
                });
        }
    }

    /**
     * @param {string} url
     * @param {string} href
     * @param {InspirationUrlData} previous_inspiration_url_data
     * @param {InspirationUrlData} next_inspiration_url_data
     * @param {boolean} update_address
     * @param {Function} callback
     */
    async getData(
        url,
        href,
        previous_inspiration_url_data,
        next_inspiration_url_data,
        update_address = true,
        callback = null
    ) {
        const timeoutMs = 5000;

        this.__insertSpinner();

        /**
         * Check if data is preloading.
         * If it is, wait for it to end (up to 5000ms).
         * If it's not (or timeout hit), get it with ajax.
         **/
        (new Promise((resolve, reject) => {
            if (url in this.preloaded_inspiration_data) {
                let timeWas = new Date();
                let wait = setInterval(() => {
                    if (this.preloaded_inspiration_data[url] !== STATUS_PRELOADING) {
                        clearInterval(wait);
                        resolve();
                    } else if (new Date() - timeWas > timeoutMs) {
                        // Timeout
                        clearInterval(wait);
                        reject();
                    }
                }, 20);
            } else {
                reject();
            }
        })).then(() => {
            /*
             * OnResolve
             */
            let html = this.preloaded_inspiration_data[url].current;
            let current_data = this.preloaded_inspiration_data[url].data;
            let current_script = this.preloaded_inspiration_data[url].script;

            document.body.classList.remove('page-loading');

            if (update_address) {
                this.updateUrl(href, current_data.name, {
                    url,
                    href,
                    previous_inspiration_url_data,
                    next_inspiration_url_data,
                    current_data,
                    type: 'modal_show'
                });
            }
            this.fillModal(html, previous_inspiration_url_data, next_inspiration_url_data);
            this.updateHeader();

            this.__insertScript(current_script, 'head');
            dataLayer.push(this.preloaded_inspiration_data[url].dataLayer);
            this.modal.show();

            if (callback) {
                callback();
            }
            this.__removeSpinner();

            this.preloadData(previous_inspiration_url_data.api);
            this.preloadData(next_inspiration_url_data.api);
        }, () => {
            /*
             * OnRejection
             */
            this.preloaded_inspiration_data[url] = STATUS_PRELOADING;

            $.get(url, {}, (data) => {
                this.preloaded_inspiration_data[url] = data;

                let html = data.current;
                let current_data = data.data;
                let current_script = data.script;

                document.body.classList.remove('page-loading');

                if (update_address) {
                    this.updateUrl(href, current_data.name, {
                        url,
                        href,
                        previous_inspiration_url_data,
                        next_inspiration_url_data,
                        current_data,
                        type: 'modal_show'
                    });
                }
                this.fillModal(html, previous_inspiration_url_data, next_inspiration_url_data);
                this.updateHeader();

                this.__insertScript(current_script, 'head');
                dataLayer.push(this.preloaded_inspiration_data[url].dataLayer);

                this.modal.show();

                if (callback) {
                    callback();
                }
                this.__removeSpinner();
            });

            this.preloadData(previous_inspiration_url_data.api);
            this.preloadData(next_inspiration_url_data.api);
        });
    };

    /**
     * @param {string} html
     * @param {InspirationUrlData} previous_inspiration_url_data
     * @param {InspirationUrlData} next_inspiration_url_data
     */
    fillModal(
        html,
        previous_inspiration_url_data,
        next_inspiration_url_data
    ) {
        const modal_body_el = this.element.querySelector('.modal-body');
        modal_body_el.innerHTML = html;

        const previous_button_el = modal_body_el.querySelector('.slider-btn.slider-btn-prev');
        if (previous_button_el) {
            previous_button_el.href = previous_inspiration_url_data.url;
            previous_button_el.dataset.href = previous_inspiration_url_data.api;

            previous_button_el.onclick = this.__getOnSliderBtnClick("previous");

            this.onClickPrev = this.__getClickToAnother(previous_button_el.href, previous_button_el.dataset.href, 'previous');

            this.onSwipePrev = this.__getSwipeToAnother(
                previous_inspiration_url_data.url,
                previous_inspiration_url_data.api,
                "previous",
            );
        } else {
            this.onClickPrev = null;
            this.onSwipePrev = null;
        }
        const next_button_el = modal_body_el.querySelector('.slider-btn.slider-btn-next');
        if (next_button_el) {
            next_button_el.href = next_inspiration_url_data.url;
            next_button_el.dataset.href = next_inspiration_url_data.api;

            next_button_el.onclick = this.__getOnSliderBtnClick("next");

            this.onClickNext = this.__getClickToAnother(next_button_el.href, next_button_el.dataset.href, 'next');

            this.onSwipeNext = this.__getSwipeToAnother(
                next_inspiration_url_data.url,
                next_inspiration_url_data.api,
                "next"
            );
        } else {
            this.onClickNext = null;
            this.onSwipeNext = null;
        }
    }

    /**
     * Modal header content doesn't get changed when new html comes from the server, so we update it manually.
     */
    updateHeader() {
        const modal_header = this.element.querySelector('.modal-header');
        const modal_header_like = modal_header.querySelector('button.like');
        const modal_header_share = modal_header.querySelector('button.share');
        const modal_body = this.element.querySelector('.modal-body');
        const modal_body_like = modal_body.querySelector('button.like');
        const modal_body_share = modal_body.querySelector('button.share');

        modal_header_like.dataset.inspirationId = modal_body_like.dataset.inspirationId;
        modal_header_like.dataset.inspirationImageUrl = modal_body_like.dataset.inspirationImageUrl;
        modal_header_share.dataset.inspirationPath = modal_body_share.dataset.inspirationPath;
    }

    /**
     * @param {"previous"|"next"} direction
     * @returns Function
     * @private
     */
    __getOnSliderBtnClick(direction) {
        return (e) => {
            e.preventDefault();
            [...e.target.closest('.slider-controls').querySelectorAll('.slider-btn')].forEach(link_el => {
                link_el.removeAttribute('href');
            });

            if (this.is_touch) {
                this.card.style.transition = 'transform .25s';
                this.card.style.transform = (direction === "next") ? `translate(-100vw)` : `translate(100vw)`;
                this.header.style.transition = 'transform .25s';
                this.header.style.transform = (direction === "next") ? `translate(-100vw)` : `translate(100vw)`;
            }

            const callback = () => {
                if (this.is_touch) {
                    this.card.style.transition = '';
                    this.card.style.transform = (direction === "next") ? `translate(100vw)` : `translate(-100vw)`;
                    this.header.style.transition = '';
                    this.header.style.transform = (direction === "next") ? `translate(100vw)` : `translate(-100vw)`;
                    setTimeout(() => {
                        this.card.style.transition = 'transform .25s';
                        this.card.style.transform = `translate(0)`;
                        this.header.style.transition = 'transform .25s';
                        this.header.style.transform = `translate(0)`;
                    }, 15);
                }
            };

            if (direction === "next") {
                this.onClickNext(callback);
            } else {
                this.onClickPrev(callback);
            }
        };
    }

    /**
     * @param {String} href
     * @param {String} url
     * @param {"previous"|"next"} direction
     * @returns Function
     * @private
     */
    __getClickToAnother(href, url, direction) {
        const shift = (direction === 'next') ? 1 : -1;
        return (callback) => {
            let previous, next;
            ({previous, next} = this.get_previous_and_next_inspiration_url_data(shift));

            if (url) {
                this.getData(url, href, previous, next, true, callback);
            }
        };
    }

    /**
     * @param {String} href
     * @param {String} url
     * @param {"previous"|"next"} direction
     * @returns Function
     * @private
     */
    __getSwipeToAnother(href, url, direction) {
        const shift = (direction === 'next') ? 1 : -1;

        /**
         * @param {Function} callback
         */
        return (callback) => {
            let previous, next;
            ({previous, next} = this.get_previous_and_next_inspiration_url_data(shift));
            this.getData(url, href, previous, next, true, callback);
        };
    }

    /**
     * @private
     */
    __insertSpinner() {
        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
            const existing_spinner = backdrop.querySelector('.spinner-grow, .spinner-border');
            if (!existing_spinner) {
                const spinner = document.createElement('div');
                spinner.classList.add('spinner-border');
                spinner.setAttribute('role', 'status');

                const spinner_info = document.createElement('span');
                spinner_info.classList.add('visually-hidden');
                spinner_info.innerText = "Loading...";

                backdrop.appendChild(spinner);
                spinner.appendChild(spinner_info);
            } else {
                existing_spinner.classList.remove('d-none');
            }
        }
    }

    /**
     * @private
     */
    __removeSpinner() {
        const backdrop = document.querySelector('.modal-backdrop');
        const existing_spinner = backdrop?.querySelector('.spinner-grow, .spinner-border');

        if (existing_spinner) {
            existing_spinner.classList.add('d-none');
        }
    }

    /**
     * HTML5 makes it tricky to insert <SCRIPT> tags.
     * @param {string} script_tag_outer_html
     * @param {string} parent_selector
     * @private
     */
    __insertScript(script_tag_outer_html, parent_selector = 'body') {
        const parser_tag = document.createElement('div');
        parser_tag.innerHTML = script_tag_outer_html;
        const temporary_script_tag = parser_tag.firstChild;

        if (temporary_script_tag && temporary_script_tag.tagName.toLowerCase() === 'script') {
            const script_element = document.createElement('script');
            script_element.innerHTML = temporary_script_tag.innerHTML;

            const parent = document.querySelector(parent_selector);
            parent.appendChild(script_element);
        }
    }

    /**
     * @param {TouchEvent} e
     */
    onTouchStart(e) {
        this.swipe_blocked = !!e.target.closest('.promo-container');
        this.start_x = e.touches[0].clientX;
        this.start_y = e.touches[0].clientY;

        this.card.style.transition = '';
        this.card.style.transform = `translate(0px)`;
        this.header.style.transition = '';
        this.header.style.transform = `translate(0px)`;
    }

    /**
     * @param {TouchEvent} e
     */
    onTouchMove(e) {
        if (this.swipe_blocked) return;

        const swipe_horizontal_length = e.touches[0].clientX - this.start_x;
        const swipe_vertical_absolute_length = Math.abs(e.touches[0].clientY - this.start_y);

        if (swipe_vertical_absolute_length <= (this.swipe_vertical_range + Math.abs(swipe_horizontal_length / 2))) {
            const translate = swipe_horizontal_length - this.swipe_start_horizontal_length;
            this.card.style.transform = `translate(${translate}px)`;
            this.header.style.transform = `translate(${translate}px)`;
        } else {
            this.swipe_blocked = true;
            this.card.style.transition = 'transform .25s';
            this.card.style.transform = `translate(0px)`;
            this.header.style.transition = 'transform .25s';
            this.header.style.transform = `translate(0px)`;
        }
    }

    /**
     * @param {TouchEvent} e
     */
    onTouchEnd(e) {
        if (this.swipe_blocked) return;

        const swipe_amount = (e.changedTouches[0].clientX - this.start_x) / window.outerWidth;

        if (swipe_amount >= this.action_ratio_threshold) {
            this.card.style.transition = 'transform .25s';
            this.card.style.transform = `translate(100vw)`;
            this.header.style.transition = 'transform .25s';
            this.header.style.transform = `translate(100vw)`;

            if (this.onSwipePrev) {
                this.onSwipePrev(() => {
                    this.card.style.transition = '';
                    this.card.style.transform = `translate(-100vw)`;
                    this.header.style.transition = '';
                    this.header.style.transform = `translate(-100vw)`;
                    setTimeout(() => {
                        this.card.style.transition = 'transform .25s';
                        this.card.style.transform = `translate(0)`;
                        this.header.style.transition = 'transform .25s';
                        this.header.style.transform = `translate(0)`;
                    }, 15);
                });
            }

        } else if (swipe_amount <= -this.action_ratio_threshold) {
            this.card.style.transition = 'transform .25s';
            this.card.style.transform = `translate(-100vw)`;
            this.header.style.transition = 'transform .25s';
            this.header.style.transform = `translate(-100vw)`;

            if (this.onSwipeNext) {
                this.onSwipeNext(() => {
                    this.card.style.transition = '';
                    this.card.style.transform = `translate(100vw)`;
                    this.header.style.transition = '';
                    this.header.style.transform = `translate(100vw)`;
                    setTimeout(() => {
                        this.card.style.transition = 'transform .25s';
                        this.card.style.transform = `translate(0)`;
                        this.header.style.transition = 'transform .25s';
                        this.header.style.transform = `translate(0)`;
                    }, 15);
                });
            }

        } else {
            this.card.style.transition = 'transform .25s';
            this.card.style.transform = `translate(0px)`;
            this.header.style.transition = 'transform .25s';
            this.header.style.transform = `translate(0px)`;
        }
    }

    onTouchCancel() {
        this.card.style.transition = 'transform .25s';
        this.card.style.transform = `translate(0px)`;
        this.header.style.transition = 'transform .25s';
        this.header.style.transform = `translate(0px)`;
    }
}

export default InspirationModal;
export {
    InspirationModal,
}
